import {setElementInnerHTML, setElementOnClick} from '../../ui_utils/DomUtils.ts'
import {type ItemData, UIComponentItemScrollCell} from './UIComponentItemScrollCell.ts'

export class UIComponentItemScroll {
    private readonly scrollWrapper: HTMLDivElement
    private readonly scrollContainer: HTMLDivElement
    private readonly leftArrow: HTMLImageElement
    private readonly rightArrow: HTMLImageElement

    private readonly items: UIComponentItemScrollCell[] = []
    private readonly scrollAmount = 500
    private readonly scrollable: boolean

    constructor(container: HTMLDivElement, itemData: ItemData[], scrollable: boolean = true) {
        this.scrollWrapper = container.querySelector('.item-scroll-wrapper') as HTMLDivElement
        this.scrollContainer = this.scrollWrapper.querySelector('.item-scroll-container') as HTMLDivElement
        this.leftArrow = container.querySelector('.scroll-arrow.left-arrow') as HTMLImageElement
        this.rightArrow = container.querySelector('.scroll-arrow.right-arrow') as HTMLImageElement

        this.scrollable = scrollable
        this.initScrolling()
        this.generateItems(itemData)
        this.updateArrowVisibility()
    }

    update(itemData: ItemData[]) {
        this.clear()
        this.generateItems(itemData)
        this.updateArrowVisibility()
        if(this.scrollable) this.scrollToSelectedItem()
    }

    updateInteractabilityOfElements(isGuest: boolean) {
        this.items.forEach((item: UIComponentItemScrollCell) => {
            item.updateInteractability(isGuest)
        })
    }

    resetScroll() {
        if(this.scrollable) this.scrollWrapper.scrollLeft = 0
        this.updateArrowVisibility()
    }

    private initScrolling() {
        if(this.scrollable) {
            setElementOnClick(this.leftArrow, () => this.scroll(-this.scrollAmount))
            setElementOnClick(this.rightArrow, () => this.scroll(this.scrollAmount))
            this.scrollWrapper.onscroll = () => this.updateArrowVisibility()
            return
        }

        this.leftArrow.style.display = 'none'
        this.rightArrow.style.display = 'none'
        this.scrollWrapper.classList.add('no-scroll')
    }

    private scroll(amount: number) {
        if(!this.scrollable) return

        const currentScroll = this.scrollWrapper.scrollLeft
        const maxScroll = this.getMaxScrollPosition()
        const newScroll = Math.max(0, Math.min(maxScroll, currentScroll + amount))

        this.scrollWrapper.scrollTo({
            left: newScroll,
            behavior: 'smooth',
        })
    }

    private getMaxScrollPosition(): number {
        return Math.max(0, this.scrollWrapper.scrollWidth - this.scrollWrapper.clientWidth)
    }

    private updateArrowVisibility() {
        if(!this.scrollable) {
            this.leftArrow.style.display = 'none'
            this.rightArrow.style.display = 'none'
            return
        }

        const {scrollLeft, scrollWidth, clientWidth} = this.scrollWrapper
        const tolerance = 1

        const canScrollLeft = scrollLeft > tolerance
        const canScrollRight = scrollLeft < scrollWidth - clientWidth - tolerance

        this.leftArrow.style.display = canScrollLeft ? 'block' : 'none'
        this.rightArrow.style.display = canScrollRight ? 'block' : 'none'
    }

    private generateItems(itemData: ItemData[]) {
        const sortedData = [...itemData]

        sortedData.sort((a, b) => {
            if(a.available === b.available) return 0
            return a.available ? -1 : 1
        })

        const priorityItem = sortedData.find((item) => item.bannerText !== undefined)
        if(priorityItem) {
            const priorityItemIndex = sortedData.indexOf(priorityItem)
            sortedData.splice(priorityItemIndex, 1)
            sortedData.splice(1, 0, priorityItem)
        }

        sortedData.forEach((item: ItemData) => {
            const itemCell = new UIComponentItemScrollCell(this.scrollContainer, item)
            this.items.push(itemCell)
        })

        if(this.scrollable) this.scrollToSelectedItem()
    }

    private scrollToSelectedItem() {
        if(!this.scrollable) return

        requestAnimationFrame(() => {
            const selectedItem = this.items.find((item) => item.container.classList.contains('selected'))
            if(!selectedItem) return

            const itemElement = selectedItem.container
            const itemWidth = itemElement.offsetWidth
            const containerWidth = this.scrollWrapper.clientWidth
            const currentScroll = this.scrollWrapper.scrollLeft

            const itemStart = itemElement.offsetLeft
            const itemEnd = itemStart + itemWidth
            const viewStart = currentScroll
            const viewEnd = currentScroll + containerWidth

            const isFullyVisible = itemStart >= viewStart && itemEnd <= viewEnd
            if(isFullyVisible) return

            const scrollPosition = itemStart - (containerWidth / 2) + (itemWidth / 2)
            this.scrollWrapper.scrollTo({
                left: Math.max(0, Math.min(scrollPosition, this.getMaxScrollPosition())),
                behavior: 'smooth',
            })
        })
    }

    private clear() {
        setElementInnerHTML(this.scrollContainer, {key: 'strings:colonistCommon.utils.emptyString'})
        this.items.length = 0
    }
}
